/**
 * DevicesPage.tsx — DEVICE <-> EMPLOYEE MANAGEMENT (v2.3.0)
 *
 * The single place Lyndon enrolls, re-assigns, retires and configures the
 * employee-tracker phones.
 *
 *   ENROLL  : pick an EMPLOYEE -> "Issue pairing code" -> read the 6-char code
 *             to the tech -> tech types it into the app -> device is BOUND to
 *             that employee. The name on the web and the name on the phone are
 *             then the same value from the same binding (no more free text).
 *   REASSIGN: change which employee a device belongs to (rotates the device
 *             token; the phone must re-pair).
 *   RETIRE  : stops collection, revokes the device's ticket access, drops it
 *             off the Map + live lists — HISTORY IS RETAINED.
 *   PURGE   : separate, destructive, type-to-confirm. Deletes all history.
 *   CONFIG  : per-device override + __global__ fleet default for every
 *             collection / feature toggle (written through the tracker's own
 *             config API — no parallel config store).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid, IconButton,
  InputLabel, MenuItem, Select, Snackbar, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon, VpnKey as KeyIcon, SwapHoriz as SwapIcon, Settings as SettingsIcon,
  Insights as InspectIcon, Apps as AppsIcon,
  PowerSettingsNew as RetireIcon, DeleteForever as PurgeIcon, LinkOff as UnbindIcon,
  Restore as RestoreIcon, ContentCopy as CopyIcon, PhoneAndroid as PhoneIcon,
  Healing as RepairIcon, AltRoute as RouteIcon,
} from '@mui/icons-material';
import { devicesApi, trackingApi } from '../services/api';
import PairingPanel from './devices/PairingPanel';
import DeviceInspector from './devices/DeviceInspector';
import FleetAppsDialog from './devices/FleetAppsDialog';
import {
  DistUnit, SmallUnit, UnitKind, getUnits, setUnits, unitLabel, toDisplay, toMetric,
  roundForInput, boundsForDisplay,
} from '../utils/units';

interface Assigned {
  user_id: number; user_name: string; email?: string;
  tracking_enabled: boolean; is_active: boolean; is_current_device: boolean;
}
interface Device {
  device_id: string; device_name?: string; employee_id?: string; employee_name?: string;
  bound_user_id?: number | null; bound_at?: string | null;
  platform?: string; model?: string; app_version?: string; status: string;
  sync_interval?: number; realtime_mode?: number;
  last_seen?: string | null; enrolled_at?: string | null; enrolled_by?: string | null;
  retired_at?: string | null; retired_by?: string | null;
  has_token: boolean; assigned_to?: Assigned | null; display_name: string; retired: boolean;
}
interface Employee { id: number; name: string; email: string; device_id?: string | null; }

const fmt = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const ago = (s?: string | null) => {
  if (!s) return 'never';
  const ms = Date.now() - new Date(s).getTime();
  if (isNaN(ms)) return 'never';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   v2.3.2 — COMPLETE SETTING REGISTRY (tracker_gps_config)
   Every column on tracker_gps_config appears here exactly once. Values are
   stored CANONICALLY IN METRIC (metres, m/s); `kind` says what the number is
   so the UI can render AND accept it in the dispatcher's chosen units.
   `min`/`max` are the same guard rails the tracker enforces server-side — the
   accuracy cutoff can no longer be dropped to 1 m.
   ═══════════════════════════════════════════════════════════════════════════ */
type Fld = {
  k: string; label: string; kind: UnitKind; min?: number; max?: number; desc?: string;
};
type Tgl = { k: string; label: string; desc?: string };
type Section = { title: string; blurb?: string; toggles?: Tgl[]; fields?: Fld[]; enums?: { k: string; label: string; values: string[]; desc?: string }[] };

const SECTIONS: Section[] = [
  {
    title: 'ADA accessibility — screen capture',
    blurb: 'Accessibility accommodation: one tap (notification or Quick-Settings tile) captures whatever is on the phone screen, any app, after the Android consent prompt; the PNG goes to Pictures/ConceptTracker and to our tracker server only. The GRANT is the SSO capability "tracker:ada_capture" on the employee (admin.concept.bz → Users). This switch can only turn it OFF for this phone (or for the fleet on Global defaults); it cannot grant.',
    toggles: [
      { k: 'ada_capture', label: 'Allow ADA screen capture on this phone', desc: 'OFF = veto even when the SSO capability is granted. Default ON (which still needs the SSO grant to do anything).' },
    ],
  },
  {
    title: 'GPS quality filters',
    blurb: 'The server-side gate every incoming fix must pass. Accuracy-first: reject junk, keep everything real.',
    toggles: [
      { k: 'enabled', label: 'GPS quality filter', desc: 'Master switch for the ingest filter. OFF = store every raw fix, junk included.' },
    ],
    fields: [
      { k: 'max_accuracy_m', label: 'Accuracy cutoff', kind: 'len_m', min: 5, max: 500,
        desc: 'Fixes whose reported accuracy is WORSE than this are dropped. Guarded: a 1 m cutoff throws away every fix and the tech goes dark.' },
      { k: 'min_distance_m', label: 'Jitter min-distance', kind: 'len_m', min: 0, max: 100,
        desc: 'Two consecutive fixes closer than this are treated as stationary jitter and merged. Keep BELOW the smallest band target spacing.' },
      { k: 'max_speed_ms', label: 'Teleport max speed', kind: 'speed_mps', min: 10, max: 200,
        desc: 'A ground fix implying more than this speed is a GPS jump and is discarded (aircraft mode exempts real flight).' },
    ],
  },
  {
    title: 'Speed bands (spatial resolution)',
    blurb: 'interval = target spacing ÷ current speed, clamped to each band\'s min/max. Sampling rate rises with speed so the trace holds a constant SPATIAL resolution. Target spacing 0 = time-based only.',
    toggles: [
      { k: 'band_enabled', label: 'Speed-banded sampling', desc: 'v2.3.1 engine. OFF = fall back to legacy adaptive intervals.' },
      { k: 'fusion_enabled', label: 'Kalman fusion (device)', desc: 'On-device sensor fusion for display + dead-reckoning.' },
      { k: 'synthetic_points_enabled', label: 'Store synthesized (dead-reckoned) points', desc: 'OFF by default: only real GNSS fixes are stored.' },
    ],
    fields: [
      { k: 'still_speed_mps', label: 'STATIONARY below', kind: 'speed_mps', min: 0.1, max: 100, desc: 'Below this speed the device is STATIONARY.' },
      { k: 'still_target_spacing_m', label: 'STATIONARY target spacing', kind: 'len_m', min: 0, max: 5000, desc: '0 = time-based only.' },
      { k: 'still_min_interval_s', label: 'STATIONARY min interval', kind: 'sec', min: 0.2, max: 3600 },
      { k: 'still_max_interval_s', label: 'STATIONARY max interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'urban_speed_mps', label: 'WALKING below', kind: 'speed_mps', min: 0.1, max: 100, desc: 'Below this speed (and above STATIONARY) = walking. Never road-snapped.' },
      { k: 'walk_target_spacing_m', label: 'WALKING target spacing', kind: 'len_m', min: 0, max: 5000 },
      { k: 'walk_min_interval_s', label: 'WALKING min interval', kind: 'sec', min: 0.2, max: 3600 },
      { k: 'walk_max_interval_s', label: 'WALKING max interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'highway_speed_mps', label: 'URBAN below', kind: 'speed_mps', min: 0.1, max: 100, desc: 'Below this speed = urban / slow driving; above = highway.' },
      { k: 'urban_target_spacing_m', label: 'URBAN target spacing', kind: 'len_m', min: 0, max: 5000 },
      { k: 'urban_min_interval_s', label: 'URBAN min interval', kind: 'sec', min: 0.2, max: 3600 },
      { k: 'urban_max_interval_s', label: 'URBAN max interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'highway_target_spacing_m', label: 'HIGHWAY target spacing', kind: 'len_m', min: 0, max: 5000 },
      { k: 'highway_min_interval_s', label: 'HIGHWAY min interval', kind: 'sec', min: 0.2, max: 3600 },
      { k: 'highway_max_interval_s', label: 'HIGHWAY max interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'band_hold_seconds', label: 'Band hold (red-light)', kind: 'sec', min: 0, max: 600,
        desc: 'Stay in the faster band this long after slowing — stops a red light from collapsing the driving cadence.' },
      { k: 'gnss_min_interval_ms', label: 'GNSS hardware floor', kind: 'ms', min: 200, max: 60000,
        desc: 'Fastest interval the chip is asked for. 1000 ms = 1 Hz, the ceiling on most phones.' },
      { k: 'fusion_process_noise', label: 'Fusion process noise', kind: 'ratio', min: 0.1, max: 20,
        desc: 'Kalman responsiveness. Lower = smoother/laggier, higher = twitchier/faster.' },
    ],
  },
  {
    title: 'Aircraft mode',
    blurb: 'Sparse by design, never road-snapped, high-speed points are NEVER discarded. Needs altitude/climb corroboration (or the speed-only trigger) so a fast car cannot trip it.',
    toggles: [
      { k: 'aircraft_mode_enabled', label: 'Aircraft mode' },
      { k: 'aircraft_require_alt', label: 'Require altitude/climb corroboration' },
      { k: 'aircraft_snap_disabled', label: 'Skip road-snapping in flight' },
    ],
    fields: [
      { k: 'aircraft_trigger_ms', label: 'Trigger speed', kind: 'speed_mps', min: 20, max: 300 },
      { k: 'aircraft_alt_trigger_m', label: 'Trigger altitude', kind: 'len_m', min: 100, max: 15000 },
      { k: 'aircraft_speed_only_mps', label: 'Speed-only trigger', kind: 'speed_mps', min: 30, max: 400,
        desc: 'Above this, altitude is not required — nothing on the ground goes this fast.' },
      { k: 'aircraft_confirm_points', label: 'Confirm over N fixes', kind: 'count', min: 1, max: 20 },
      { k: 'aircraft_target_spacing_m', label: 'Target spacing', kind: 'len_m', min: 0, max: 5000 },
      { k: 'aircraft_min_interval_s', label: 'Min interval', kind: 'sec', min: 0.2, max: 3600 },
      { k: 'aircraft_max_interval_s', label: 'Max interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'aircraft_accuracy_m', label: 'Accuracy cutoff in flight', kind: 'len_m', min: 10, max: 5000,
        desc: 'Relaxed exemption: GPS is poor at altitude, so the normal cutoff would delete the whole flight.' },
      { k: 'aircraft_speed_ms', label: 'Max plausible speed in flight', kind: 'speed_mps', min: 50, max: 1000 },
    ],
  },
  {
    title: 'Sensor gating (power)',
    blurb: 'Idles the GNSS chip while the phone is genuinely still; motion wakes it. A guaranteed fallback check runs regardless, so coverage never drops.',
    toggles: [
      { k: 'sensor_gate_enabled', label: 'Sensor-gated GPS' },
    ],
    fields: [
      { k: 'still_hold_seconds_before_gps_sleep', label: 'Still hold before GPS sleeps', kind: 'sec', min: 10, max: 3600 },
      { k: 'motion_wake_sensitivity', label: 'Motion wake sensitivity', kind: 'ratio', min: 0.1, max: 10,
        desc: 'Accelerometer threshold multiplier. Lower = wakes on the slightest movement.' },
      { k: 'still_fallback_check_seconds', label: 'Still fallback check', kind: 'sec', min: 10, max: 3600,
        desc: 'Even when asleep, take a fix at least this often. This is the coverage floor.' },
    ],
  },
  {
    title: 'Sync & queue',
    blurb: 'How points get off the phone. Opportunistic sync piggybacks on a radio that is already up (free); the timed batch flush is the guaranteed backstop.',
    toggles: [
      { k: 'opportunistic_flush_enabled', label: 'Opportunistic sync' },
      { k: 'passive_location_enabled', label: 'Passive capture (free fixes from other apps)' },
    ],
    fields: [
      { k: 'realtime_interval_sec', label: 'Realtime push interval', kind: 'sec', min: 1, max: 600 },
      { k: 'batch_flush_count', label: 'Batch flush at N points', kind: 'count', min: 1, max: 500 },
      { k: 'batch_flush_secs', label: 'Batch flush interval', kind: 'sec', min: 5, max: 3600 },
      { k: 'moving_flush_secs', label: 'Flush interval while moving', kind: 'sec', min: 1, max: 600 },
      { k: 'max_queue_age_seconds', label: 'Max queue age', kind: 'sec', min: 10, max: 86400,
        desc: 'A queued point older than this forces a flush even with no radio opportunity.' },
    ],
  },
  {
    title: 'Legacy adaptive sampling',
    blurb: 'The pre-v2.3.1 distance/interval engine. Still the fallback whenever speed-banded sampling is OFF.',
    toggles: [
      { k: 'adaptive_enabled', label: 'Adaptive sampling' },
    ],
    fields: [
      { k: 'trigger_distance_m', label: 'Distance trigger', kind: 'len_m', min: 1, max: 1000 },
      { k: 'min_spacing_m', label: 'Min spacing', kind: 'len_m', min: 0, max: 1000 },
      { k: 'still_interval_s', label: 'Still interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'walk_interval_s', label: 'Walk interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'drive_interval_s', label: 'Drive interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'fast_interval_s', label: 'Fast interval', kind: 'sec', min: 1, max: 3600 },
      { k: 'walk_speed_mps', label: 'Walk threshold', kind: 'speed_mps', min: 0.1, max: 100 },
      { k: 'drive_speed_mps', label: 'Drive threshold', kind: 'speed_mps', min: 0.1, max: 100 },
      { k: 'fast_speed_mps', label: 'Fast threshold', kind: 'speed_mps', min: 0.1, max: 100 },
    ],
  },
  {
    title: 'Road snapping & map display',
    toggles: [
      { k: 'road_snapping_enabled', label: 'Road snapping (this device)', desc: 'Snap this device\'s driving trace to roads. Match radius lives in Route pipeline.' },
      { k: 'low_accuracy_shading_enabled', label: 'Low-accuracy uncertainty circle' },
    ],
    fields: [
      { k: 'low_accuracy_threshold_m', label: 'Uncertainty-circle threshold', kind: 'len_m', min: 5, max: 1000,
        desc: 'A fix worse than this gets a shaded circle on the Map.' },
    ],
  },
  {
    title: 'Logging',
    blurb: 'The rotating on-device radio / opportunistic-sync log.',
    fields: [
      { k: 'log_max_entries', label: 'Log max entries', kind: 'count', min: 50, max: 10000 },
      { k: 'log_max_age_hours', label: 'Log retention (hours)', kind: 'count', min: 1, max: 720 },
    ],
  },
  {
    title: 'Units on the phone',
    blurb: 'What the TECH sees in the app. The dispatcher\'s own units (top-right of this dialog) are separate and local to this browser.',
    enums: [
      { k: 'units_distance', label: 'Distance / mileage', values: ['mi', 'km'] },
      { k: 'units_small', label: 'Accuracy / small distance', values: ['ft', 'm'] },
    ],
  },
];

// ── v2.3.4 SYNC (WiFi preference) + SMART STOP DETECTION ───────────────────
// WiFi is PREFERRED for every sync/upload; cellular is the fallback. Large
// uploads (photos/signatures) can be held for WiFi, but "Max queue age (s)"
// above is the guaranteed FLOOR: an aged item goes out on cellular anyway, so
// nothing ever sits forever.
const SYNC_BOOLS: [string, string][] = [
  ['prefer_wifi_enabled', 'Prefer WiFi over cellular for all sync'],
  ['large_uploads_wifi_only', 'Photos/signatures: wait for WiFi (until max queue age)'],
  ['stop_prompt_enabled', 'Prompt the tech to classify a stop'],
];
const STOP_NUMS: [string, string][] = [
  ['stop_site_match_radius_m', 'Job-site match radius (m)'],
  ['stop_prompt_min_duration_sec', 'Min stop duration before prompting (s)'],
];

const COLLECT_BOOLS: [string, string][] = [
  ['collect_location', 'Location'],
  ['collect_sensors', 'Sensors'],
  ['collect_calls', 'Call logs'],
  ['collect_sms', 'SMS metadata'],
  ['collect_web_history', 'Web history'],
  ['collect_notifications', 'Notifications'],
  ['collect_app_usage', 'App usage'],
  ['collect_clipboard', 'Clipboard'],
  ['collect_screen_content', 'Screen content'],
];

/* ── Unit-aware number field ──────────────────────────────────────────────
   Displays the METRIC value converted into the active unit, and converts what
   the user types back to METRIC before it ever reaches state / the API.
   Out-of-range values are flagged (and blocked on save) rather than silently
   accepted — this is the guard that makes a 1 m accuracy cutoff impossible. */
function UnitField({
  fld, value, units, onChange, testid,
}: {
  fld: Fld; value: any; units: { distance: DistUnit; small: SmallUnit };
  onChange: (metric: number | '') => void; testid?: string;
}) {
  const [text, setText] = useState<string>('');
  const [focus, setFocus] = useState(false);
  const suffix = unitLabel(fld.kind, units);
  const b = boundsForDisplay(fld.min, fld.max, fld.kind, units);

  useEffect(() => {
    if (focus) return;                       // never fight the user's cursor
    const d = roundForInput(toDisplay(value, fld.kind, units), fld.kind);
    setText(d === '' ? '' : String(d));
  }, [value, units.distance, units.small, focus]);   // eslint-disable-line

  const metric = toMetric(text, fld.kind, units);
  const bad = text !== '' && metric !== '' &&
    ((fld.min !== undefined && Number(metric) < fld.min) || (fld.max !== undefined && Number(metric) > fld.max));

  const help = bad
    ? `Out of range — allowed ${b.min?.toFixed(2)} to ${b.max?.toFixed(2)} ${suffix}`
    : fld.desc;

  return (
    <Tooltip title={fld.desc || ''} placement="top" arrow>
      <TextField
        size="small" fullWidth type="number" error={bad}
        inputProps={{ 'data-testid': testid || `fld-${fld.k}`, step: 'any' }}
        label={suffix ? `${fld.label} (${suffix})` : fld.label}
        value={text}
        helperText={help}
        FormHelperTextProps={{ sx: { fontSize: 10, lineHeight: 1.2, m: 0, mt: 0.3 } }}
        onFocus={() => setFocus(true)}
        onBlur={() => { setFocus(false); }}
        onChange={(e) => {
          setText(e.target.value);
          onChange(toMetric(e.target.value, fld.kind, units));
        }}
      />
    </Tooltip>
  );
}

/* ── Route-pipeline (system_settings gps_*) field metadata comes from the API
      itself (GET /tracking/gps-settings returns type/unit/min/max/desc), so
      there is exactly one definition of the route knobs. ─────────────────── */
const RP_KIND: Record<string, UnitKind> = {
  m: 'len_m', ft: 'len_ft', mph: 'speed_mph',
};

export default function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // pairing (v2.4.0 — TTL, live countdown, outstanding codes, revoke)
  const [codeOpen, setCodeOpen] = useState(false);

  // v2.4.0 — live status for every device, polled. This is what makes the page
  // an operations console instead of a static list.
  const [status, setStatus] = useState<Record<string, any>>({});
  const [inspect, setInspect] = useState<Device | null>(null);
  const [fleetApps, setFleetApps] = useState(false);

  // assign dialog
  const [assignOpen, setAssignOpen] = useState<Device | null>(null);
  const [assignEmp, setAssignEmp] = useState<number | ''>('');

  // purge dialog
  const [purgeOpen, setPurgeOpen] = useState<Device | null>(null);
  const [purgeConfirm, setPurgeConfirm] = useState('');

  // config dialog
  const [cfgOpen, setCfgOpen] = useState<string | null>(null); // device_id or '__global__'
  const [cfg, setCfg] = useState<any>(null);
  const [cfgBusy, setCfgBusy] = useState(false);

  // v2.3.2 — dispatcher's own display/entry units (shared with the Map via
  // localStorage). Flipping this re-renders every number below AND changes what
  // unit the input boxes accept. Storage stays metric.
  const [units, setUnitsState] = useState(getUnits());
  const flipUnits = (u: Partial<{ distance: DistUnit; small: SmallUnit }>) => {
    setUnits(u);
    setUnitsState(getUnits());
  };

  // v2.3.2 — route pipeline (dispatch system_settings gps_*): the server-side
  // route/matching stage. Editable here so Lyndon never needs SQL.
  const [rpOpen, setRpOpen] = useState(false);
  const [rp, setRp] = useState<any[] | null>(null);
  const [rpVals, setRpVals] = useState<Record<string, string>>({});
  const [rpBusy, setRpBusy] = useState(false);

  // Rendered in the page header AND inside each dialog, so the unit can be
  // flipped while a config dialog is open (a modal covers the header).
  const UnitsPicker = ({ idSuffix = '' }: { idSuffix?: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, border: '1px solid #e5e7eb', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">My units</Typography>
      <Select size="small" variant="standard" disableUnderline data-testid={`units-distance${idSuffix}`}
        value={units.distance} onChange={(e) => flipUnits({ distance: e.target.value as DistUnit })}
        sx={{ fontSize: 13, fontWeight: 700 }}>
        <MenuItem value="mi">mi / mph</MenuItem>
        <MenuItem value="km">km / km/h</MenuItem>
      </Select>
      <Select size="small" variant="standard" disableUnderline data-testid={`units-small${idSuffix}`}
        value={units.small} onChange={(e) => flipUnits({ small: e.target.value as SmallUnit })}
        sx={{ fontSize: 13, fontWeight: 700 }}>
        <MenuItem value="ft">ft</MenuItem>
        <MenuItem value="m">m</MenuItem>
      </Select>
    </Box>
  );

  const openRoutePipeline = async () => {
    setRpOpen(true);
    setRp(null);
    try {
      const r = await trackingApi.getGpsSettings();
      const list = r.data.settings || [];
      setRp(list);
      setRpVals(Object.fromEntries(list.map((x: any) => [x.key, String(x.value ?? x.default ?? '')])));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not load route settings');
      setRpOpen(false);
    }
  };

  const saveRoutePipeline = async () => {
    setRpBusy(true);
    try {
      await trackingApi.updateGpsSettings(rpVals);
      setToast('Route pipeline settings saved — they apply on the next route load');
      setRpOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally {
      setRpBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, e] = await Promise.all([devicesApi.list(), devicesApi.employees()]);
      setDevices(d.data.devices || []);
      setEmployees(e.data.employees || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Live status: one cheap call for the whole fleet, every 20 s.
  const loadStatus = useCallback(async () => {
    try {
      const r = await devicesApi.status();
      const m: Record<string, any> = {};
      for (const d of (r.data.devices || [])) m[d.device_id] = d;
      setStatus(m);
    } catch { /* a status blip must never take the page down */ }
  }, []);

  useEffect(() => {
    void loadStatus();
    const t = setInterval(() => { void loadStatus(); }, 20000);
    return () => clearInterval(t);
  }, [loadStatus]);

  const act = async (fn: () => Promise<any>, msg: string) => {
    try {
      await fn();
      setToast(msg);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Action failed');
    }
  };

  const openConfig = async (deviceId: string) => {
    setCfgOpen(deviceId);
    setCfg(null);
    try {
      const r = await devicesApi.getConfig(deviceId);
      const d = r.data;
      setCfg({
        gps: { ...(d.gps_global || {}), ...(d.gps || {}) },
        collection: d.collection || {},
        realtime_mode: !!d.device?.realtime_mode,
        has_override: d.has_override,
        is_global: deviceId === '__global__',
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not load config');
      setCfgOpen(null);
    }
  };

  // Client-side guard rails — the tracker enforces the same bounds server-side,
  // this just gives an instant, readable reason instead of a round-trip.
  const cfgViolations = (): string[] => {
    if (!cfg?.gps) return [];
    const out: string[] = [];
    for (const sec of SECTIONS) {
      for (const f of sec.fields || []) {
        const v = cfg.gps[f.k];
        if (v === '' || v === null || v === undefined) continue;
        const n = Number(v);
        if (!isFinite(n)) { out.push(`${f.label} must be a number`); continue; }
        if (f.min !== undefined && n < f.min) out.push(`${f.label}: below the safe minimum`);
        if (f.max !== undefined && n > f.max) out.push(`${f.label}: above the safe maximum`);
      }
    }
    return out;
  };

  const saveConfig = async () => {
    if (!cfgOpen || !cfg) return;
    const v = cfgViolations();
    if (v.length) { setError(v.join(' • ')); return; }
    setCfgBusy(true);
    try {
      const payload: any = { gps: cfg.gps };
      if (cfgOpen !== '__global__') {
        payload.collection = cfg.collection;
        payload.realtime_mode = cfg.realtime_mode;
      }
      await devicesApi.putConfig(cfgOpen, payload);
      setToast(cfgOpen === '__global__' ? 'Global default saved' : 'Device config saved');
      setCfgOpen(null);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally {
      setCfgBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Devices</Typography>
          <Typography variant="body2" color="text.secondary">
            Pair a phone to an employee with a one-time code. The name here is the name the tech sees in the app.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-testid="issue-code-btn" variant="contained" startIcon={<KeyIcon />} onClick={() => setCodeOpen(true)}
            sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}>
            Issue pairing code
          </Button>
          <Button data-testid="fleet-apps-btn" variant="outlined" startIcon={<AppsIcon />} onClick={() => setFleetApps(true)}>
            Apps on phones
          </Button>
          <Button data-testid="global-defaults-btn" variant="outlined" startIcon={<SettingsIcon />} onClick={() => openConfig('__global__')}>
            Global defaults
          </Button>
          <Button data-testid="route-pipeline-btn" variant="outlined" startIcon={<RouteIcon />} onClick={openRoutePipeline}>
            Route &amp; map pipeline
          </Button>
          <UnitsPicker />
          <Tooltip title="Re-stamp every paired device with its employee's real name">
            <Button variant="outlined" startIcon={<RepairIcon />}
              onClick={() => act(() => devicesApi.repairIdentity(), 'Identities repaired')}>
              Repair identity
            </Button>
          </Tooltip>
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Live</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Device</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>App</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Last seen</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>Enrolled</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : devices.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No devices yet — issue a pairing code to enroll one.</Typography>
              </TableCell></TableRow>
            ) : devices.map((d) => (
              <TableRow key={d.device_id} hover data-testid="device-row" sx={{ opacity: d.retired ? 0.55 : 1 }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color={d.retired ? 'disabled' : 'primary'} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.display_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {d.assigned_to?.email || (d.bound_user_id ? `user ${d.bound_user_id}` : 'not paired')}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell data-testid="live-cell">
                  {(() => {
                    const s = status[d.device_id];
                    if (!s) return <Chip size="small" label="—" variant="outlined" />;
                    return (
                      <Box>
                        <Chip size="small" label={s.online ? 'ONLINE' : 'OFFLINE'}
                          color={s.online ? 'success' : 'default'}
                          variant={s.online ? 'filled' : 'outlined'} />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {s.battery_level != null
                            ? `${Math.round(Number(s.battery_level) * (Number(s.battery_level) <= 1 ? 100 : 1))}%`
                            : '—'}
                          {' · '}{s.network_type || '—'}
                          {s.sync_backlog ? ` · ${s.sync_backlog} queued` : ''}
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">{d.device_name || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{d.device_id}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip size="small" label={d.app_version || '—'} variant="outlined" />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2">{ago(d.last_seen)}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmt(d.last_seen)}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Typography variant="body2">{d.enrolled_at ? fmt(d.enrolled_at) : '—'}</Typography>
                  {d.enrolled_by && <Typography variant="caption" color="text.secondary">by {d.enrolled_by}</Typography>}
                </TableCell>
                <TableCell>
                  {d.retired ? (
                    <Tooltip title={`Retired ${fmt(d.retired_at)} — history retained`}>
                      <Chip size="small" label="RETIRED" color="default" />
                    </Tooltip>
                  ) : d.has_token ? (
                    <Tooltip title="Paired by code — tracks AND opens tickets with no password">
                      <Chip size="small" label="PAIRED" color="success" />
                    </Tooltip>
                  ) : d.assigned_to ? (
                    <Tooltip title="Assigned to an employee and tracking, but never redeemed a pairing code. Issue one so the tech can open tickets without a password.">
                      <Chip size="small" label="ASSIGNED" color="info" />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Not bound to any employee">
                      <Chip size="small" label="UNPAIRED" color="warning" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title="Inspect — live status, the data this phone has sent, and the other apps on it">
                      <IconButton size="small" color="primary" data-testid="inspect-btn"
                        onClick={() => setInspect(d)}>
                        <InspectIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!d.retired && (
                      <>
                        <Tooltip title="Re-assign to another employee">
                          <IconButton size="small" onClick={() => { setAssignEmp(d.bound_user_id || ''); setAssignOpen(d); }}>
                            <SwapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Unbind (history retained)">
                          <IconButton size="small" onClick={() => act(() => devicesApi.unassign(d.device_id), 'Device unbound')}>
                            <UnbindIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Collection + feature config">
                          <IconButton size="small" onClick={() => openConfig(d.device_id)}>
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Retire — stops collection, keeps history">
                          <IconButton size="small" color="warning"
                            onClick={() => { if (window.confirm(`Retire ${d.device_id}?\n\nCollection stops and it leaves the Map. ALL HISTORY IS KEPT.`)) act(() => devicesApi.retire(d.device_id), 'Device retired (history retained)'); }}>
                            <RetireIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {d.retired && (
                      <Tooltip title="Reactivate">
                        <IconButton size="small" color="success" onClick={() => act(() => devicesApi.reactivate(d.device_id), 'Device reactivated')}>
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="PURGE — permanently delete device AND all history">
                      <IconButton size="small" color="error" onClick={() => { setPurgeConfirm(''); setPurgeOpen(d); }}>
                        <PurgeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---------- pairing (v2.4.0) ---------- */}
      <PairingPanel
        open={codeOpen}
        onClose={() => { setCodeOpen(false); void load(); }}
        employees={employees}
        onChanged={() => { void load(); void loadStatus(); }}
      />

      {/* ---------- per-device inspector: status / data / apps ---------- */}
      <DeviceInspector
        deviceId={inspect?.device_id || null}
        displayName={inspect?.display_name}
        onClose={() => setInspect(null)}
      />

      {/* ---------- fleet-wide app roll-up ---------- */}
      <FleetAppsDialog open={fleetApps} onClose={() => setFleetApps(false)} />

      {/* ---------- reassign ---------- */}
      <Dialog open={!!assignOpen} onClose={() => setAssignOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Re-assign device</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{assignOpen?.device_id}</Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Employee</InputLabel>
            <Select label="Employee" value={assignEmp} onChange={(e) => setAssignEmp(e.target.value as number)}>
              {employees.map((e) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Re-assigning rotates the device token. The phone must re-pair with a new code to keep ticket access.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignOpen(null)}>Cancel</Button>
          <Button variant="contained" disabled={!assignEmp}
            onClick={() => { const d = assignOpen!; setAssignOpen(null); act(() => devicesApi.assign(d.device_id, Number(assignEmp)), 'Device re-assigned'); }}>
            Re-assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- purge ---------- */}
      <Dialog open={!!purgeOpen} onClose={() => setPurgeOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#b91c1c' }}>Purge device — destructive</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This permanently deletes the device AND every location, sensor, clock and activity record it ever
            produced. This is NOT retire. There is no undo.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>Type the device id to confirm:</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{purgeOpen?.device_id}</Typography>
          <TextField fullWidth size="small" sx={{ mt: 1 }} value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPurgeOpen(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={purgeConfirm !== purgeOpen?.device_id}
            onClick={() => { const d = purgeOpen!; setPurgeOpen(null); act(() => devicesApi.purge(d.device_id), 'Device purged'); }}>
            Purge everything
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- config ---------- */}
      <Dialog open={!!cfgOpen} onClose={() => setCfgOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            {cfgOpen === '__global__' ? 'Global collection defaults (whole fleet)' : 'Device collection & features'}
            {cfgOpen !== '__global__' && (
              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{cfgOpen}</Typography>
            )}
          </Box>
          <UnitsPicker idSuffix="-cfg" />
        </DialogTitle>
        <DialogContent dividers>
          {!cfg ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <>
              {cfgOpen !== '__global__' && !cfg.has_override && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This device currently inherits the GLOBAL defaults. Saving creates a per-device override.
                </Alert>
              )}
              <Alert severity="success" icon={false} sx={{ mb: 2, py: 0.5 }}>
                <Typography variant="caption">
                  Everything below is stored in <b>metric</b> and shown/entered in <b>{units.small} / {units.distance === 'km' ? 'km · km/h' : 'mi · mph'}</b>.
                  Change the unit with “My units” at the top of the page and every box here re-renders and accepts that unit.
                  Values outside the safe range are rejected.
                </Typography>
              </Alert>

              {SECTIONS.map((sec) => (
                <Box key={sec.title} data-testid={`section-${sec.title}`} sx={{ mb: 2.5, p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{sec.title}</Typography>
                  {sec.blurb && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{sec.blurb}</Typography>
                  )}
                  {sec.toggles && (
                    <Grid container spacing={1} sx={{ mb: sec.fields ? 1 : 0 }}>
                      {sec.toggles.map((t) => (
                        <Grid item xs={12} sm={6} key={t.k}>
                          <Tooltip title={t.desc || ''} placement="top" arrow>
                            <FormControlLabel
                              control={<Switch size="small" data-testid={`tgl-${t.k}`} checked={!!Number(cfg.gps?.[t.k])}
                                onChange={(e) => setCfg({ ...cfg, gps: { ...cfg.gps, [t.k]: e.target.checked ? 1 : 0 } })} />}
                              label={<Typography variant="body2">{t.label}</Typography>}
                            />
                          </Tooltip>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  {sec.fields && (
                    <Grid container spacing={2}>
                      {sec.fields.map((f) => (
                        <Grid item xs={12} sm={6} md={4} key={f.k}>
                          <UnitField fld={f} value={cfg.gps?.[f.k]} units={units}
                            onChange={(metric) => setCfg({ ...cfg, gps: { ...cfg.gps, [f.k]: metric } })} />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  {sec.enums && (
                    <Grid container spacing={2}>
                      {sec.enums.map((en) => (
                        <Grid item xs={12} sm={6} key={en.k}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>{en.label}</InputLabel>
                            <Select label={en.label} data-testid={`enum-${en.k}`} value={cfg.gps?.[en.k] ?? en.values[0]}
                              onChange={(e) => setCfg({ ...cfg, gps: { ...cfg.gps, [en.k]: e.target.value } })}>
                              {en.values.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Sync &amp; stop detection (v2.3.4)</Typography>
              <Alert severity="info" sx={{ my: 1 }}>
                <b>WiFi is preferred for every sync and upload</b>; cellular is the fallback. Photos and
                signatures may be held for WiFi &mdash; but <b>Max queue age (s)</b> above is the guaranteed
                floor, so an aged upload still goes out on cellular. Nothing sits forever.<br />
                A stop within the <b>job-site match radius</b> of one of the tech&apos;s ASSIGNED tickets is
                offered as WORK; anything else is personal / lunch-break / gas. Stops shorter than the
                minimum duration never prompt.
              </Alert>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {SYNC_BOOLS.map(([k, label]) => (
                  <Grid item xs={12} sm={6} key={k}>
                    <FormControlLabel
                      control={<Switch size="small" checked={!!Number(cfg.gps?.[k])}
                        onChange={(e) => setCfg({ ...cfg, gps: { ...cfg.gps, [k]: e.target.checked ? 1 : 0 } })} />}
                      label={<Typography variant="body2">{label}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={2}>
                {STOP_NUMS.map(([k, label]) => (
                  <Grid item xs={6} sm={4} md={3} key={k}>
                    <TextField size="small" fullWidth type="number" label={label}
                      value={cfg.gps?.[k] ?? ''}
                      onChange={(e) => setCfg({ ...cfg, gps: { ...cfg.gps, [k]: e.target.value } })} />
                  </Grid>
                ))}
                <Grid item xs={6} sm={4} md={3}>
                  <TextField size="small" fullWidth select SelectProps={{ native: true }}
                    label="Fuel volume the tech types"
                    value={cfg.gps?.fuel_volume_units ?? 'gal'}
                    onChange={(e) => setCfg({ ...cfg, gps: { ...cfg.gps, fuel_volume_units: e.target.value } })}>
                    <option value="gal">gallons (gal)</option>
                    <option value="L">litres (L)</option>
                  </TextField>
                </Grid>
              </Grid>

              {cfgOpen !== '__global__' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>What this device collects</Typography>
                  <Grid container spacing={1}>
                    {COLLECT_BOOLS.map(([k, label]) => (
                      <Grid item xs={12} sm={6} md={4} key={k}>
                        <FormControlLabel
                          control={<Switch size="small" checked={!!Number(cfg.collection?.[k])}
                            onChange={(e) => setCfg({ ...cfg, collection: { ...cfg.collection, [k]: e.target.checked ? 1 : 0 } })} />}
                          label={<Typography variant="body2">{label}</Typography>}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={<Switch size="small" checked={!!cfg.realtime_mode}
                          onChange={(e) => setCfg({ ...cfg, realtime_mode: e.target.checked })} />}
                        label={<Typography variant="body2">Realtime mode (live Map)</Typography>}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCfgOpen(null)}>Cancel</Button>
          <Button variant="contained" disabled={!cfg || cfgBusy} onClick={saveConfig}>
            {cfgBusy ? <CircularProgress size={18} /> : 'Save config'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- route + map pipeline (dispatch system_settings gps_*) ---------- */}
      <Dialog open={rpOpen} onClose={() => setRpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            Route &amp; map pipeline (fleet-wide)
            <Typography variant="caption" display="block" color="text.secondary">
              Server-side processing of a stored trace into the drawn route: quality filters, Kalman, road
              snapping, stop detection, aircraft handling and the Map's display defaults.
            </Typography>
          </Box>
          <UnitsPicker idSuffix="-rp" />
        </DialogTitle>
        <DialogContent dividers>
          {!rp ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <>
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                <Typography variant="caption">
                  Shown and entered in <b>{units.small} / {units.distance === 'km' ? 'km/h' : 'mph'}</b>; stored canonically.
                  Changes apply on the next route load.
                </Typography>
              </Alert>
              {Array.from(new Set(rp.map((x: any) => x.group))).map((grp) => (
                <Box key={grp as string} data-testid={`rp-${grp}`} sx={{ mb: 2, p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{grp as string}</Typography>
                  <Grid container spacing={2}>
                    {rp.filter((x: any) => x.group === grp).map((x: any) => {
                      if (x.type === 'bool') {
                        return (
                          <Grid item xs={12} sm={6} key={x.key}>
                            <Tooltip title={x.desc || ''} placement="top" arrow>
                              <FormControlLabel
                                control={<Switch size="small" data-testid={`rp-tgl-${x.key}`} checked={rpVals[x.key] === 'true'}
                                  onChange={(e) => setRpVals({ ...rpVals, [x.key]: e.target.checked ? 'true' : 'false' })} />}
                                label={<Typography variant="body2">{x.label}</Typography>}
                              />
                            </Tooltip>
                          </Grid>
                        );
                      }
                      if (x.type === 'enum') {
                        return (
                          <Grid item xs={12} sm={6} key={x.key}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>{x.label}</InputLabel>
                              <Select label={x.label} value={rpVals[x.key] ?? x.default}
                                onChange={(e) => setRpVals({ ...rpVals, [x.key]: String(e.target.value) })}>
                                {(x.values || []).map((v: string) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </Grid>
                        );
                      }
                      const kind: UnitKind = RP_KIND[x.unit as string] || 'none';
                      const fld: Fld = { k: x.key, label: x.label, kind, min: x.min, max: x.max, desc: x.desc };
                      return (
                        <Grid item xs={12} sm={6} md={4} key={x.key}>
                          <UnitField fld={fld} units={units} value={rpVals[x.key]} testid={`rp-fld-${x.key}`}
                            onChange={(metric) => setRpVals({ ...rpVals, [x.key]: metric === '' ? '' : String(metric) })} />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRpOpen(false)}>Cancel</Button>
          <Button variant="contained" data-testid="rp-save" disabled={!rp || rpBusy} onClick={saveRoutePipeline}>
            {rpBusy ? <CircularProgress size={18} /> : 'Save route settings'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
}
